import { Controller } from "@hotwired/stimulus"
import { get } from "@rails/request.js"

// Connects to data-controller="pass-search"
//
// Type-ahead search for the passes list. Typing (from 3 characters) fetches
// matching passes and shows a suggestions dropdown WITHOUT reloading the list,
// so the caret is never disturbed mid-typing. Picking a suggestion sets the
// hidden id filter and submits the filter form. Clearing the box removes the
// filter.
export default class extends Controller {
  static targets = ["input", "results", "idInput", "form"]
  static values = { minChars: { type: Number, default: 3 } }

  connect() {
    // Power-user shortcut: "/" focuses the search box (unless already typing in
    // a field).
    this.onKeydown = (e) => {
      const tag = document.activeElement?.tagName
      if (e.key === "/" && tag !== "INPUT" && tag !== "TEXTAREA" && tag !== "SELECT") {
        e.preventDefault()
        this.inputTarget.focus()
      }
    }
    document.addEventListener("keydown", this.onKeydown)
  }

  disconnect() {
    document.removeEventListener("keydown", this.onKeydown)
  }

  async search() {
    const q = this.inputTarget.value.trim()

    // Re-typing invalidates any previously picked pass.
    if (this.hasIdInputTarget && this.idInputTarget.value) {
      this.idInputTarget.value = ""
    }

    if (q.length < this.minCharsValue) {
      this.clearResults()
      // Emptying the box after having filtered should clear the filter.
      if (q.length === 0 && this.element.dataset.hadFilter === "true") {
        this.element.dataset.hadFilter = "false"
        this.submit()
      }
      return
    }

    clearTimeout(this.timeout)
    this.timeout = setTimeout(async () => {
      const season = this.currentSeasonId()
      const url = `/passes/search.json?query=${encodeURIComponent(q)}` +
        (season ? `&season_id=${encodeURIComponent(season)}` : "")
      const response = await get(url, { responseKind: "json" })
      if (response.ok) this.showResults(await response.json)
    }, 200)
  }

  showResults(passes) {
    this.resultsTarget.replaceChildren()

    if (!passes.length) {
      const li = document.createElement("li")
      li.className = "list-group-item text-muted small"
      li.textContent = "Sin coincidencias"
      this.resultsTarget.appendChild(li)
      return
    }

    // Build each row with DOM APIs (textContent / dataset) rather than HTML
    // string templating, so values can never break out of markup.
    for (const p of passes) {
      const li = document.createElement("li")
      li.className = "list-group-item list-group-item-action"
      li.setAttribute("role", "button")
      li.dataset.action = "mousedown->pass-search#select"
      li.dataset.id = p.id
      li.dataset.name = p.name || ""

      const name = document.createElement("span")
      name.className = "fw-medium"
      name.textContent = p.name || ""
      li.appendChild(name)

      if (p.company) {
        const company = document.createElement("small")
        company.className = "text-muted d-block"
        company.textContent = p.company
        li.appendChild(company)
      }

      this.resultsTarget.appendChild(li)
    }
  }

  select(event) {
    event.preventDefault()
    const { id, name } = event.currentTarget.dataset

    if (this.hasIdInputTarget) this.idInputTarget.value = id
    this.inputTarget.value = name
    this.element.dataset.hadFilter = "true"
    this.clearResults()
    this.submit()
  }

  keydown(event) {
    if (event.key === "Enter") {
      event.preventDefault()
      const first = this.resultsTarget.querySelector("[data-id]")
      if (first) first.dispatchEvent(new Event("mousedown"))
    } else if (event.key === "Escape") {
      this.clearResults()
    }
  }

  submit() {
    // Mirror the forms controller: build the query from the form (now including
    // the hidden id filter) and fetch the turbo-stream, so the list updates
    // without a full navigation. The selected season is respected as-is.
    const form = this.formTarget
    const url = new URL(form.action)
    const params = new URLSearchParams(
      [...new FormData(form).entries()].filter(([, v]) => v !== "")
    )
    requestAnimationFrame(() => {
      get(`${url.pathname}?${params}`, { responseKind: "turbo-stream" })
    })
  }

  // Season currently chosen in the filter form ("" when "Todas las temporadas").
  currentSeasonId() {
    const field = this.formTarget.querySelector("[name='q[season_id_eq]']")
    return field ? field.value : ""
  }

  clearResults() {
    setTimeout(() => this.resultsTarget.replaceChildren(), 150)
  }
}
